import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const MultiCartContext = createContext();

const MultiCartContextProvider = (props) => {
    // Dynamic URL based on environment
    const getApiUrl = () => {
        const hostname = window.location.hostname;
        const isDevelopment = hostname === 'localhost' || hostname === '127.0.0.1';
        
        if (isDevelopment) {
            // Use the proxy in development to avoid CORS issues
            return '';
        } else {
            // Remove trailing slash if present to avoid double slashes
            const backendUrl = (import.meta.env.VITE_BACKEND_URL || 'https://dashbasket.onrender.com');
            return backendUrl.replace(/\/$/, '');
        }
    };
    
    const url = getApiUrl();
    const { triggerLogin } = useAuth();
    
    const [restaurantCarts, setRestaurantCarts] = useState([]);
    const [savedCarts, setSavedCarts] = useState([]);
    const [sharedCarts, setSharedCarts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    // Request deduplication and caching
    const activeRequests = useRef(new Set());
    const lastFetchTime = useRef(0);
    const CACHE_DURATION = 5000; // 5 seconds
    const FETCH_DEBOUNCE_MS = 1000; // 1 second debounce

    // Get token from localStorage
    const getToken = () => localStorage.getItem("token");

    // Fetch all user carts
    const fetchUserCarts = useCallback(async () => {
        const requestKey = 'fetch_user_carts';
        const now = Date.now();
        
        if (activeRequests.current.has(requestKey)) {
            console.log('MultiCartContext: Cart fetch request already in progress, skipping duplicate');
            return;
        }
        
        if (now - lastFetchTime.current < FETCH_DEBOUNCE_MS) {
            console.log('MultiCartContext: Cart fetch request debounced');
            return;
        }

        try {
            setLoading(true);
            activeRequests.current.add(requestKey);
            lastFetchTime.current = now;
            
            const token = getToken();
            if (!token) return;

            const response = await axios.get(`${url}/api/multi-cart`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                const serverCarts = (response.data.carts || []).filter(c => Array.isArray(c.items) && c.items.length > 0);
                setRestaurantCarts(prev => {
                    const idOf = c => (c.restaurantId?._id || c.restaurantId);
                    const byId = new Map(serverCarts.map(c => [idOf(c), c]));
                    const merged = [...serverCarts];
                    (prev || []).forEach(c => {
                        const key = idOf(c);
                        const hasItems = Array.isArray(c.items) && c.items.length > 0;
                        if (hasItems && !byId.has(key)) merged.push(c);
                    });
                    return merged;
                });
                setSavedCarts(response.data.savedCarts || []);
                setSharedCarts(response.data.sharedCarts || []);
            }
        } catch (error) {
            console.error("Error fetching carts:", error);
            setError(error.response?.data?.message || "Failed to fetch carts");
        } finally {
            setLoading(false);
            activeRequests.current.delete(requestKey);
        }
    }, [url]);

    // Optimistic add item to restaurant cart
    const addToRestaurantCart = async (itemId, restaurantId, itemMeta) => {
        try {
            const token = getToken();
            if (!token) {
                triggerLogin();
                return false;
            }

            // Optimistic local update: create cart and add item immediately
            setRestaurantCarts(prev => {
                const carts = Array.isArray(prev) ? [...prev] : [];
                const idOf = v => (v && typeof v === 'object' && v._id) ? v._id : v;
                let cart = carts.find(c => idOf(c.restaurantId) === restaurantId);
                if (!cart) {
                    cart = {
                        restaurantId,
                        restaurantName: itemMeta?.restaurantName || 'Restaurant',
                        restaurantImage: itemMeta?.restaurantImage || null,
                        items: [],
                        subtotal: 0
                    };
                    carts.push(cart);
                }
                const item = cart.items.find(i => idOf(i.foodId) === itemId);
                if (item) {
                    item.quantity += 1;
                } else {
                    cart.items.push({
                        foodId: itemId,
                        quantity: 1,
                        price: itemMeta?.price ?? 0,
                        name: itemMeta?.name ?? '',
                        image: itemMeta?.image ?? ''
                    });
                }
                cart.subtotal = cart.items.reduce((t, i) => t + (Number(i.price) * Number(i.quantity)), 0);
                return carts;
            });

            // Server sync
            const response = await axios.post(`${url}/api/multi-cart/add`, 
                { itemId, restaurantId },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                const carts = (response.data.carts || []).filter(c => Array.isArray(c.items) && c.items.length > 0);
                setRestaurantCarts(prev => {
                    // Merge to preserve optimistic cart if server still empty
                    const idOf = v => (v && typeof v === 'object' && v._id) ? v._id : v;
                    const byId = new Map(carts.map(c => [idOf(c.restaurantId), c]));
                    const merged = [...carts];
                    (prev || []).forEach(c => {
                        const key = idOf(c.restaurantId);
                        if (Array.isArray(c.items) && c.items.length > 0 && !byId.has(key)) {
                            merged.push(c);
                        }
                    });
                    return merged;
                });
                return true;
            }

            return true; // keep optimistic
        } catch (error) {
            console.error("Error adding to cart:", error);
            setError(error.response?.data?.message || "Failed to add to cart");
            return false;
        }
    };

    // Remove item from restaurant cart (optimistic + 404 tolerant)
    const removeFromRestaurantCart = async (itemId, restaurantId) => {
        try {
            const token = getToken();
            if (!token) return false;

            // Optimistic local update
            setRestaurantCarts(prev => {
                const idOf = v => (v && typeof v === 'object' && v._id) ? v._id : v;
                const carts = Array.isArray(prev) ? [...prev] : [];
                const idx = carts.findIndex(c => idOf(c.restaurantId) === restaurantId);
                if (idx >= 0) {
                    const cart = { ...carts[idx], items: [...(carts[idx].items || [])] };
                    const itemIdx = cart.items.findIndex(i => idOf(i.foodId) === itemId);
                    if (itemIdx >= 0) {
                        if (Number(cart.items[itemIdx].quantity) > 1) {
                            cart.items[itemIdx] = { ...cart.items[itemIdx], quantity: Number(cart.items[itemIdx].quantity) - 1 };
                        } else {
                            cart.items.splice(itemIdx, 1);
                        }
                        cart.subtotal = (cart.items || []).reduce((t, i) => t + (Number(i.price) * Number(i.quantity)), 0);
                        if (cart.items.length === 0) {
                            carts.splice(idx, 1);
                        } else {
                            carts[idx] = cart;
                        }
                    }
                }
                return carts;
            });

            // Server sync
            const response = await axios.delete(`${url}/api/multi-cart/remove/${restaurantId}/${itemId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data?.success) {
                const serverCarts = (response.data.carts || []).filter(c => Array.isArray(c.items) && c.items.length > 0);
                setRestaurantCarts(prev => {
                    const idOf = v => (v && typeof v === 'object' && v._id) ? v._id : v;
                    const byId = new Map(serverCarts.map(c => [idOf(c.restaurantId), c]));
                    const merged = [...serverCarts];
                    (prev || []).forEach(c => {
                        const key = idOf(c.restaurantId);
                        if (Array.isArray(c.items) && c.items.length > 0 && !byId.has(key)) merged.push(c);
                    });
                    return merged;
                });
                return true;
            }

            return true; // keep optimistic state
        } catch (error) {
            // Treat 404 (already removed) as success
            if (error?.response?.status === 404) {
                console.warn('MultiCartContext: remove 404 -> treating as already removed');
                return true;
            }
            console.error("Error removing from cart:", error);
            setError(error.response?.data?.message || "Failed to remove from cart");
            return false;
        }
    };

    // Clear specific restaurant cart (optimistic + 404 tolerant)
    const clearRestaurantCart = async (restaurantId) => {
        try {
            const token = getToken();
            if (!token) return false;

            // Optimistic local clear
            setRestaurantCarts(prev => (prev || []).filter(c => (c.restaurantId?._id || c.restaurantId) !== restaurantId));

            const response = await axios.delete(`${url}/api/multi-cart/clear/${restaurantId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data?.success) {
                const carts = (response.data.carts || []).filter(c => Array.isArray(c.items) && c.items.length > 0);
                setRestaurantCarts(carts);
                return true;
            }
            return true; // keep optimistic
        } catch (error) {
            if (error?.response?.status === 404) {
                console.warn('MultiCartContext: clear 404 -> treating as already cleared');
                return true;
            }
            console.error("Error clearing cart:", error);
            setError(error.response?.data?.message || "Failed to clear cart");
            return false;
        }
    };

    // Clear all carts
    const clearAllCarts = async () => {
        try {
            const token = getToken();
            if (!token) return false;

            const response = await axios.delete(`${url}/api/multi-cart/clear-all`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setRestaurantCarts([]);
                return true;
            }
        } catch (error) {
            console.error("Error clearing all carts:", error);
            setError(error.response?.data?.message || "Failed to clear all carts");
            return false;
        }
    };

    // Save cart for later
    const saveCart = async (restaurantId, cartName, description = "", isPublic = false) => {
        try {
            const token = getToken();
            if (!token) return null;

            const response = await axios.post(`${url}/api/multi-cart/save`,
                { restaurantId, cartName, description, isPublic },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                // Update state directly instead of refetching to avoid loops
                setSavedCarts(prev => [...prev, response.data.savedCart]);
                return {
                    savedCart: response.data.savedCart,
                    shareId: response.data.shareId
                };
            }
        } catch (error) {
            console.error("Error saving cart:", error);
            setError(error.response?.data?.message || "Failed to save cart");
            return null;
        }
    };

    // Share cart
    const shareCart = async (savedCartId) => {
        try {
            const token = getToken();
            if (!token) return null;

            const response = await axios.post(`${url}/api/multi-cart/share/${savedCartId}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                return {
                    shareId: response.data.shareId,
                    shareUrl: response.data.shareUrl
                };
            }
        } catch (error) {
            console.error("Error sharing cart:", error);
            setError(error.response?.data?.message || "Failed to share cart");
            return null;
        }
    };

    // Get shared cart by ID (public endpoint)
    const getSharedCart = async (shareId) => {
        try {
            const response = await axios.get(`${url}/api/multi-cart/shared/${shareId}`);
            
            if (response.data.success) {
                return {
                    sharedCart: response.data.sharedCart,
                    sharedBy: response.data.sharedBy
                };
            }
        } catch (error) {
            console.error("Error fetching shared cart:", error);
            setError(error.response?.data?.message || "Failed to fetch shared cart");
            return null;
        }
    };

    // Add shared cart to user's collection
    const addSharedCart = async (shareId) => {
        try {
            const token = getToken();
            if (!token) return false;

            const response = await axios.post(`${url}/api/multi-cart/add-shared`,
                { shareId },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                // Update state directly instead of refetching to avoid loops
                if (response.data.carts) {
                    setRestaurantCarts(response.data.carts);
                }
                return true;
            }
        } catch (error) {
            console.error("Error adding shared cart:", error);
            setError(error.response?.data?.message || "Failed to add shared cart");
            return false;
        }
    };

    // Load shared cart into active carts
    const loadSharedCart = async (shareId) => {
        try {
            const token = getToken();
            if (!token) return false;

            const response = await axios.post(`${url}/api/multi-cart/load-shared`,
                { shareId },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setRestaurantCarts(response.data.carts || []);
                return true;
            }
        } catch (error) {
            console.error("Error loading shared cart:", error);
            setError(error.response?.data?.message || "Failed to load shared cart");
            return false;
        }
    };

    // Calculate totals for a specific restaurant cart
    const getRestaurantCartTotal = (restaurantCart) => {
        const currency = "BIF";
        const deliveryCharge = 1000; // Default delivery fee

        const subtotal = restaurantCart.subtotal || 0;
        const tax = 0; // No tax - included in item prices
        const total = subtotal + tax + deliveryCharge;

        return {
            subtotal,
            tax,
            deliveryCharge,
            total,
            currency
        };
    };

    // Get total number of items across all carts
    const getTotalItemCount = () => {
        return restaurantCarts.reduce((total, cart) => {
            return total + cart.items.reduce((itemTotal, item) => itemTotal + item.quantity, 0);
        }, 0);
    };

    // Format image URL helper
    const formatImageUrl = (imgUrl) => {
        if (!imgUrl) return null;
        
        if (imgUrl.startsWith('data:') || imgUrl.startsWith('blob:')) {
            return imgUrl;
        }
        
        if (imgUrl.startsWith('/uploads/')) {
            return `${url}${imgUrl}`;
        } else if (!imgUrl.startsWith('http')) {
            return `${url}/uploads/${imgUrl}`;
        }
        
        return imgUrl;
    };

    // Utility function to ensure consistent string keys for React components
    const getStringKey = (value, fallback = 'unknown') => {
        if (!value) return fallback;
        if (typeof value === 'string') return value;
        
        // Handle populated MongoDB documents
        if (typeof value === 'object' && value._id) {
            return value._id.toString();
        }
        
        if (value && typeof value.toString === 'function') return value.toString();
        return fallback;
    };

    // Load carts on component mount and when token changes
    useEffect(() => {
        const token = getToken();
        if (token) {
            fetchUserCarts();
        }
    }, [fetchUserCarts]);

    // Clear error after 5 seconds
    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => setError(null), 5000);
            return () => clearTimeout(timer);
        }
    }, [error]);

    const contextValue = {
        // State
        restaurantCarts,
        savedCarts,
        sharedCarts,
        loading,
        error,
        url,

        // Actions
        fetchUserCarts,
        addToRestaurantCart,
        removeFromRestaurantCart,
        clearRestaurantCart,
        clearAllCarts,
        saveCart,
        shareCart,
        getSharedCart,
        addSharedCart,
        loadSharedCart,

        // Utilities
        getRestaurantCartTotal,
        getTotalItemCount,
        formatImageUrl,
        getStringKey,
        setError
    };

    return (
        <MultiCartContext.Provider value={contextValue}>
            {props.children}
        </MultiCartContext.Provider>
    );
};

export default MultiCartContextProvider;

// Custom hook to use the MultiCart context
export const useMultiCart = () => {
    const context = useContext(MultiCartContext);
    if (context === undefined) {
        throw new Error('useMultiCart must be used within a MultiCartContextProvider');
    }
    return context;
}; 